// src/components/admin/payouts/PayoutsManagement.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { Button, Table, Modal } from "react-bootstrap";
import {
  FiSearch,
  FiEye,
  FiX,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiDollarSign,
  FiUser,
  FiMail,
  FiHash,
  FiCalendar,
  FiTrendingUp,
  FiCheck,
  FiXCircle,
  FiActivity,
  FiUpload,
  FiMoreVertical,
} from "react-icons/fi";
import AppPagination from "../../../components/general/pagination/Pagination.js";
import { FaBuilding, FaCreditCard, FaUniversity } from "react-icons/fa";
import "../../../assets/css/superadmin/payoutmanagement/PayoutManagement.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import AppContext from "../../../context/AppContext.js";

const AVATAR_COLORS = [
  "#FDB714",
  "#FFD166",
  "#F4A261",
  "#6BCB77",
  "#4D96FF",
  "#C77DFF",
  "#FF6B9D",
  "#E76F51",
];

const STATUS_FILTERS = ["All Status", "Pending", "In Progress", "Completed"];

const TABS = [
  {
    label: "Pending",
    icon: <FiClock size={14} />,
    status: "pending",
  },
  {
    label: "Accepted",
    icon: <FiCheckCircle size={14} />,
    status: "in-progress",
  },
  {
    label: "Rejected",
    icon: <FiXCircle size={14} />,
    status: "rejected",
  },
  {
    label: "Paid",
    icon: <FiDollarSign size={14} />,
    status: "completed",
  },
];

// ── Backend Srpayout record ko table/modal k mutabiq shape mein convert karta hai ──
const mapPayout = (p) => {
  const sellerName =
    p.rentaladmin?.fullname || p.rentaladmin?.business_name || "Unknown Seller";

  const sellerEmail = p.rentaladmin?.email || "N/A";
  const sellerImage = p.rentaladmin?.image || null; // ✅ NEW
  const initials = sellerName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return {
    id: `PAY-${String(p.id).padStart(4, "0")}`,
    rawId: p.id,
    seller: {
      name: sellerName,
      email: sellerEmail,
      image: sellerImage, // ✅ NEW
      initials,
      colorIdx: p.id % AVATAR_COLORS.length,
    },
    net: `Rs.${Number(p.amount).toLocaleString("en-PK")}`,
    netRaw: Number(p.amount),
    date: p.created_at ? p.created_at.split("T")[0] : "",
    status: p.status,
    bankName: p.bankname || "N/A",
    accountTitle: p.accounttitle || "N/A",
    accountNumber: p.accountnumber || "N/A",
    paymentMethod: p.paymentmethod || "N/A",
    category: p.category || "Withdrawal Request",
    bookings: p.bookings || 0,
    remarks: p.remarks || "",
    reciptupload: p.reciptupload || null,
  };
};

const getStatusClass = (status) => {
  switch (status) {
    case "completed":
      return "pm-badge-completed";
    case "pending":
      return "pm-badge-pending";
    case "in-progress":
      return "pm-badge-processing";
    case "rejected":
      return "pm-badge-rejected";
    default:
      return "pm-badge-pending";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "pending":
      return "Pending";

    case "in-progress":
      return "Accepted";

    case "rejected":
      return "Rejected";

    case "completed":
      return "Paid";

    default:
      return status;
  }
};

// ─── SKELETON COMPONENTS ───
const SkeletonStatCards = () => (
  <div className="row g-3 mb-4">
    {[1, 2, 3, 4].map((i) => (
      <div className="col-6 col-lg-3" key={i}>
        <div className="pm-skeleton-stat-card">
          <div className="pm-skeleton pm-skeleton-stat-label" />
          <div className="pm-skeleton pm-skeleton-stat-value" />
          <div className="pm-skeleton pm-skeleton-stat-sub" />
          <div className="pm-skeleton pm-skeleton-stat-icon" />
        </div>
      </div>
    ))}
  </div>
);

const SkeletonTabs = () => (
  <div className="pm-skeleton-tabs-wrap">
    {[1, 2, 3].map((i) => (
      <div key={i} className="pm-skeleton pm-skeleton-tab" />
    ))}
  </div>
);

const SkeletonTableCard = () => (
  <div className="pm-skeleton-table-card">
    <div className="pm-skeleton-table-heading">
      <div className="pm-skeleton pm-skeleton-table-title" />
    </div>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="pm-skeleton-table-row">
        <div className="pm-skeleton pm-skeleton-cell pm-skeleton-cell-id" />
        <div className="pm-skeleton pm-skeleton-avatar" />
        <div className="pm-skeleton-seller-info">
          <div className="pm-skeleton pm-skeleton-seller-name" />
          <div className="pm-skeleton pm-skeleton-seller-email" />
        </div>
        <div className="pm-skeleton pm-skeleton-cell pm-skeleton-cell-amount" />
        <div className="pm-skeleton pm-skeleton-cell pm-skeleton-cell-date" />
        <div className="pm-skeleton pm-skeleton-badge" />
        <div className="pm-skeleton pm-skeleton-action" />
      </div>
    ))}
  </div>
);

// ─── STAT CARD COMPONENT ───
const StatCard = ({ stat }) => (
  <div className="pm-stat-card">
    <div className="pm-stat-card-top">
      <div>
        <p className="pm-stat-label">{stat.label}</p>
        <h3 className="pm-stat-value">{stat.value}</h3>
        <p className="pm-stat-sub">{stat.sub}</p>
      </div>
      <div
        className="pm-stat-icon"
        style={{
          background: stat.iconBg,
          color: stat.iconColor,
        }}
      >
        {stat.icon}
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════

const PayoutsManagement = () => {
  const { baseUrl, baseUrlForFile ,authToken, loggedInAdmin} = useContext(AppContext);
  const isModerator = loggedInAdmin?.issuper === 'moderator';

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // ── Payouts ab backend se aayenge ──
  const [payoutsData, setPayoutsData] = useState([]);

  // ── Admin remarks (Accept/Reject k waqt) ──
  const [adminRemarks, setAdminRemarks] = useState("");

  // ── Receipt Upload ──
  const fileInputRef = useRef(null);
  const [uploadingPayout, setUploadingPayout] = useState(null);

  // ── View All Payouts (GET) ──
  const viewAllPayouts = () => {
    setIsLoading(true);
    axios
      .get(`${baseUrl}/superadmin/payout/fetchAllPayouts`, {
      headers: { Authorization: `Bearer ${authToken}` }

      })
      .then((res) => {
        const list = res.data.payoutrequests || [];
        setPayoutsData(list.map(mapPayout));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    viewAllPayouts();
  }, []);

  const totalCount = payoutsData.length;

  const pendingCount = payoutsData.filter((p) => p.status === "pending").length;

  const acceptedCount = payoutsData.filter(
    (p) => p.status === "in-progress",
  ).length;

  const rejectedCount = payoutsData.filter(
    (p) => p.status === "rejected",
  ).length;

  const paidCount = payoutsData.filter((p) => p.status === "completed").length;

  const STATS = [
    {
      label: "Total Payouts",
      value: totalCount,
      sub: "all requests",
      iconBg: "#FFF3CD",
      iconColor: "#FDB714",
      icon: <FiDollarSign size={22} />,
    },

    {
      label: "Pending",
      value: pendingCount,
      sub: "awaiting approval",
      iconBg: "#FEF9C3",
      iconColor: "#A16207",
      icon: <FiClock size={22} />,
    },

    {
      label: "Accepted",
      value: acceptedCount,
      sub: "approved requests",
      iconBg: "#DCFCE7",
      iconColor: "#22C55E",
      icon: <FiCheckCircle size={22} />,
    },

    {
      label: "Rejected",
      value: rejectedCount,
      sub: "declined requests",
      iconBg: "#FEE2E2",
      iconColor: "#DC2626",
      icon: <FiXCircle size={22} />,
    },
    {
      label: "Paid",
      value: paidCount,
      sub: "payment completed",
      iconBg: "#E0F2FE",
      iconColor: "#0284C7",
      icon: <FiDollarSign size={22} />,
    },
  ];

  const filtered = payoutsData.filter((p) => {
    const matchTab = p.status === activeTab;
    const matchStatus =
      statusFilter === "All Status" ||
      p.status === statusFilter.toLowerCase().replace(" ", "-");
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      p.id.toLowerCase().includes(q) ||
      p.seller.name.toLowerCase().includes(q) ||
      p.seller.email.toLowerCase().includes(q);
    return matchTab && matchStatus && matchSearch;
  });

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  React.useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".pm-dropdown-wrapper")) setActiveDropdown(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const toggleDropdown = (e, id) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleView = (p) => {
    setSelectedPayout(p);
    setAdminRemarks(p.remarks || "");
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedPayout(null);
    setAdminRemarks("");
  };

  // ── Status Update (Accept / Reject / Mark as Paid) ──
  const updatePayoutStatus = (payout, newStatus, remarksText = "") => {
    axios
      .post(`${baseUrl}/superadmin/payout/updateStatus`, {
        id: payout.rawId,
        status: newStatus,
        remarks: remarksText,
      }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }
    )
      .then(() => {
        viewAllPayouts();
        setActiveDropdown(null);
        handleClose();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleAccept = () => {
    if (!selectedPayout) return;
    updatePayoutStatus(selectedPayout, "in-progress", adminRemarks);
  };

  const handleReject = () => {
    if (!selectedPayout) return;
    updatePayoutStatus(selectedPayout, "rejected", adminRemarks);
  };

  const handleMarkPaid = (p) => {
    updatePayoutStatus(p, "completed");
  };

  // ── Receipt Upload ──
  const handleUpload = (p) => {
    setUploadingPayout(p);
    setActiveDropdown(null);
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingPayout) return;

    const formData = new FormData();
    formData.append("id", uploadingPayout.rawId);
    formData.append("receipt", file);

    axios
      .post(`${baseUrl}/superadmin/payout/uploadReceipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" ,
          Authorization: `Bearer ${authToken}`},
      })
      .then(() => {
        viewAllPayouts();
        setUploadingPayout(null);
      })
      .catch((err) => {
        console.log(err);
      });

    e.target.value = "";
  };

  const avatarBg = selectedPayout
    ? AVATAR_COLORS[selectedPayout.seller.colorIdx] || "#FDB714"
    : "#FDB714";

  return (
    <MasterLayout>
      {/* ── Page Header ── */}
      <div className="pm-header">
        <h1>Payouts Management</h1>
        <p>
          Process seller payments and manage all payout requests efficiently.
        </p>
      </div>

      {isLoading ? (
        <>
          <SkeletonStatCards />
          <SkeletonTabs />
          <SkeletonTableCard />
        </>
      ) : (
        <>
          {/* ── Stats Cards ── */}

          <div className="pm-stats-grid mb-4">
            {STATS.map((s, i) => (
              <StatCard stat={s} key={i} />
            ))}
          </div>

          {/* ── Tabs ── */}
          <div className="pm-outer-tabs-wrap">
            <div className="pm-outer-tabs">
              {TABS.map((tab) => {
                const count = payoutsData.filter(
                  (p) => p.status === tab.status,
                ).length;
                return (
                  <button
                    key={tab.status}
                    className={`pm-outer-tab ${activeTab === tab.status ? "pm-outer-tab-active" : ""}`}
                    onClick={() => {
                      setActiveTab(tab.status);
                      setCurrentPage(1);
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                    <span className="pm-outer-tab-count">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Table Card ── */}
          <div className="pm-table-card">
            <div className="pm-table-heading">
              <h2 className="pm-table-title">
                {activeTab === "pending" && "Pending Payouts"}

                {activeTab === "in-progress" && "Accepted Payouts"}

                {activeTab === "rejected" && "Rejected Payouts"}

                {activeTab === "completed" && "Paid Payouts"}
              </h2>
            </div>

            <div className="pm-table-wrapper">
              <Table className="pm-table">
                <thead>
                  <tr>
                    <th className="pm-col-id">PAYOUT ID</th>
                    <th className="pm-col-seller">RENTALADMIN NAME</th>
                    <th className="pm-col-amount">TOTAL AMOUNT</th>
                    <th className="pm-col-date">DATE</th>
                    <th className="pm-col-status">STATUS</th>
                    <th className="pm-col-action">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="pm-empty-row">
                        No payout records found.
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <span className="pm-payout-id">{p.id}</span>
                        </td>
                        <td>
                          {/* ✅ Naya: */}
                          <div className="pm-seller-cell">
                            <img
                              src={`${baseUrlForFile}/${p.seller.image}`}
                              alt=""
                              className="pm-avatar-img"
                            />
                            <div className="pm-seller-info">
                              <div className="pm-seller-name">{p.seller.name}</div>
                              <div className="pm-seller-email">{p.seller.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="pm-amount net">{p.net}</span>
                        </td>
                        <td>
                          <span className="pm-date-col">{p.date}</span>
                        </td>
                        <td>
                          <span
                            className={`pm-badge ${getStatusClass(p.status)}`}
                          >
                            <span className="pm-badge-dot"></span>
                            {getStatusLabel(p.status)}
                          </span>
                        </td>
                        <td className="pm-text-center">
                          <div className="pm-dropdown-wrapper">
                            <button
                              className="pm-dots-btn"
                              onClick={(e) => toggleDropdown(e, p.id)}
                            >
                              <FiMoreVertical size={18} />
                            </button>
                            {activeDropdown === p.id && (
                              <div className="pm-custom-dropdown-menu">
                                <button
                                  className="pm-custom-dropdown-item"
                                  onClick={() => handleView(p)}
                                >
                                  <FiEye size={15} className="pm-item-view" />{" "}
                                  View Detail
                                </button>
                                <div className="pm-custom-dropdown-divider" />
                                <button
                                  className="pm-custom-dropdown-item"
                                  onClick={() => handleMarkPaid(p)}
                                >
                                  <FiCheck
                                    size={15}
                                    className="pm-item-accept"
                                  />{" "}
                                  Mark as Paid
                                </button>
                                <button
                                  className="pm-custom-dropdown-item"
                                  onClick={() => handleUpload(p)}
                                >
                                  <FiUpload
                                    size={15}
                                    className="pm-item-upload"
                                  />{" "}
                                  Receipt Upload
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <AppPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filtered.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </>
      )}

      {/* ════ PAYOUT DETAIL MODAL ════ */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        className="pm-detail-modal"
      >
        {selectedPayout && (
          <>
            <div className="pm-modal-header">
              <div className="pm-modal-header-left">
                <h5>Payout Detail</h5>
                <span>Full breakdown of this payout request</span>
              </div>
              <Button className="pm-modal-close" onClick={handleClose}>
                <FiX />
              </Button>
            </div>

            <Modal.Body className="pm-modal-body">
              <div className="pm-modal-status-bar">
                <div className="sb-left">
                  <FiHash size={15} color="#FDB714" />
                  <span className="sb-id">{selectedPayout.id}</span>
                  <span
                    className={`pm-badge ${getStatusClass(selectedPayout.status)}`}
                  >
                    <span className="pm-badge-dot"></span>
                    {getStatusLabel(selectedPayout.status)}
                  </span>
                </div>
                <div
                  style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}
                >
                  <FiCalendar size={13} style={{ marginRight: 5 }} />
                  {selectedPayout.date}
                </div>
              </div>

              <p className="pm-section-title">
                <FiUser size={14} /> Seller Information
              </p>
              {/* ✅ Naya: */}
              <div className="pm-modal-seller-strip">
                <img
                  src={`${baseUrlForFile}/${selectedPayout.seller.image}`}
                  alt=""
                  className="pm-avatar-img pm-avatar-img-lg"
                />
                <div style={{ flex: 1 }}>
                  <div className="ms-name">{selectedPayout.seller.name}</div>
                  <div className="ms-email">
                    <FiMail size={11} style={{ marginRight: 4 }} />
                    {selectedPayout.seller.email}
                  </div>
                </div>
                <span className={`pm-badge ${getStatusClass(selectedPayout.status)}`}>
                  <span className="pm-badge-dot"></span>
                  {getStatusLabel(selectedPayout.status)}
                </span>
              </div>

              <p className="pm-section-title">
                <FiHash size={14} /> Payout Details
              </p>
              <div className="pm-info-grid">
                <div className="pm-info-item">
                  <label>Payout ID</label>
                  <span
                    style={{
                      color: "var(--secondary)",
                      fontFamily: "'Baloo 2', cursive",
                    }}
                  >
                    {selectedPayout.id}
                  </span>
                </div>
                <div className="pm-info-item">
                  <label>Category</label>
                  <span>{selectedPayout.category}</span>
                </div>
                <div className="pm-info-item">
                  <label>Payment Method</label>
                  <span>{selectedPayout.paymentMethod}</span>
                </div>
                <div className="pm-info-item">
                  <label>Date</label>
                  <span>{selectedPayout.date}</span>
                </div>
              </div>

              <p className="pm-section-title">
                <FaUniversity size={14} /> Bank Details
              </p>
              <div className="pm-bank-grid">
                <div className="pm-bank-item">
                  <div className="pm-bank-icon-wrap">
                    <FaBuilding size={16} color="#FDB714" />
                  </div>
                  <div>
                    <label>Bank Name</label>
                    <span>{selectedPayout.bankName}</span>
                  </div>
                </div>
                <div className="pm-bank-item">
                  <div className="pm-bank-icon-wrap">
                    <FiUser size={16} color="#FDB714" />
                  </div>
                  <div>
                    <label>Account Title</label>
                    <span>{selectedPayout.accountTitle}</span>
                  </div>
                </div>
                <div className="pm-bank-item pm-bank-item-full">
                  <div className="pm-bank-icon-wrap">
                    <FaCreditCard size={16} color="#FDB714" />
                  </div>
                  <div>
                    <label>Account Number</label>
                    <span className="pm-account-number">
                      {selectedPayout.accountNumber}
                    </span>
                  </div>
                </div>
              </div>

              <p className="pm-section-title">
                <FiDollarSign size={14} /> Total Amount
              </p>
              <div className="pm-financials-box">
                <div className="pm-fin-row total">
                  <span>Total Payout Amount</span>
                  <span>{selectedPayout.net}</span>
                </div>
              </div>

              {/* ✅ Remarks — Accept/Reject k waqt bhi yehi bhejay jayenge */}
              <p className="pm-section-title">
                <FiAlertCircle size={14} /> Remarks
              </p>
              <textarea
                className="pm-remarks-textarea"
                rows={3}
                placeholder="Add remarks (optional)"
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "1px solid #E5E5E5",
                  padding: 10,
                  fontSize: 13,
                }}
              />

              {/* ✅ Agar receipt upload ho chuki hai to link dikhao */}
              {selectedPayout.reciptupload && (
                <div className="pm-notes-box" style={{ marginTop: 12 }}>
                  <FiUpload
                    size={14}
                    color="#FDB714"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>
                    <strong>Receipt: </strong>
                    <a
                      href={`${baseUrl}/uploads/payouts/${selectedPayout.reciptupload}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Uploaded Receipt
                    </a>
                  </span>
                </div>
              )}
            </Modal.Body>

            {/* ✅ Accept / Reject sirf Pending payouts k liye, aur sirf superadmin k liye */}
            {selectedPayout.status === "pending" && (
              isModerator ? (
                <div style={{ padding: "16px 24px", borderTop: "1px solid #F0F0F0", fontSize: 13, color: "#999" }}>
                  View only — only a superadmin can approve or reject payouts.
                </div>
              ) : (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "16px 24px",
                  borderTop: "1px solid #F0F0F0",
                }}
              >
                <Button
                  className="pm-accept-btn"
                  style={{ flex: 1 }}
                  onClick={handleAccept}
                >
                  <FiCheck size={15} /> Accept
                </Button>
                <Button
                  className="pm-reject-btn"
                  style={{ flex: 1 }}
                  onClick={handleReject}
                >
                  <FiXCircle size={15} /> Reject
                </Button>
              </div>
              )
            )}
          </>
        )}
      </Modal>

      {/* ── Hidden file input — Receipt Upload k liye ── */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*,.pdf"
        onChange={handleFileChange}
      />
    </MasterLayout>
  );
};

export default PayoutsManagement;
